import { Fragment } from "react"
import { RotateCcw } from "lucide-react"

import { PanelCard, PanelCardHead, PanelCardBody } from "./PanelCard"
import StatusBadge from "../StatusBadge"
import { Button } from "@/components/ui/button"
import { cn } from "@/lib/utils"

// The four nodes on the revision progress line, and which one each server
// stage lights (order.revisions.stage, UPDA-95). The nodes before the lit one
// are done. A paused round (on hold / cancelled) stops at "Requested" and lets
// the status cell say why. The line used to light "Being revised" and the
// status cell used to say "In progress" whatever the round's real state, so a
// round the freelancer had returned — or one already approved on a completed
// order — still read as running.
const STEPS = ["Requested", "Being revised", "Ready for review", "Approved"]
const STAGE_NODE = { paused: 0, revising: 1, review: 2, approved: 3 }

// One node on the revision progress line.
function Step({ label, state = "pending" }) {
  return (
    <div
      data-step={label}
      data-state={state}
      className="flex items-center gap-[9px] text-[11.5px] font-semibold text-muted-foreground"
    >
      <span
        className={cn(
          "size-2.5 rounded-full",
          state === "done" && "bg-chart-2",
          state === "now" && "bg-chart-2 ring-4 ring-[color-mix(in_oklab,var(--chart-2)_25%,transparent)]",
          state === "pending" && "bg-line-2"
        )}
      />
      {label}
    </div>
  )
}

// Revision Requests tab — driven by the order's real revision allowance. With no
// revision yet it shows the empty state + how many rounds are included; once a
// revision exists it shows that round's detail. Mirrors order-revisions.html.
export default function RevisionsPanel({ order, onOpenModal }) {
  // A payload without a stage (older than this field) keeps the neutral
  // "In progress" rather than a claim the server did not make.
  const { used = 0, max, stage = "revising", statusLabel = "In progress", rounds = [] } = order.revisions ?? {}
  // The rounds the customer asked for, newest first, each with the exact note
  // they typed (UPDA-104). The latest one names the round in the summary; a
  // payload without rounds (older than this field) keeps the numbered label.
  const latest = rounds.length ? rounds[rounds.length - 1] : null
  const remaining = max != null ? Math.max(max - used, 0) : null
  const now = STAGE_NODE[stage] ?? STAGE_NODE.revising
  // The server-computed eligibility gate (in an approval phase, customer, and
  // revisions remaining). The control must never be active in an ineligible
  // state; the server remains the final guard (AQ-011).
  const canRequest = Boolean(order.permissions?.requestRevision)

  // The empty state only while nothing was ever asked. `used` follows the
  // allowance counter for the CURRENT phase (draft rounds while writing, then
  // OrderRevisions), so it drops back to 0 once writing finishes; the rounds
  // the customer asked for are history and must stay listed regardless.
  if (used === 0 && rounds.length === 0) {
    return (
      <PanelCard>
        <PanelCardHead Icon={RotateCcw} title="Revision Requests">
          <StatusBadge tier="in_progress" label={max != null ? `0 of ${max} used` : "No revisions"} />
        </PanelCardHead>
        <PanelCardBody className="py-14 text-center">
          <div className="mx-auto mb-4 grid size-14 place-items-center rounded-[15px] border border-border bg-secondary">
            <RotateCcw className="size-6 text-ink-3" strokeWidth={1.7} />
          </div>
          <h3 className="font-display text-[15px] font-bold">
            No revisions requested yet
          </h3>
          <p className="mx-auto mt-1.5 max-w-[360px] text-[12.5px] leading-[1.5] text-ink-3">
            {canRequest
              ? (max != null
                ? `This order includes ${max} revision ${max === 1 ? "round" : "rounds"}. Request changes once your draft is ready for review.`
                : "Request changes once your draft is ready for review.")
              : "You can request changes once the freelancer submits work for review."}
          </p>
          {canRequest && (
            <Button className="mt-5 gap-[7px] font-bold" onClick={() => onOpenModal?.("request-revision")}>
              <RotateCcw className="size-4" strokeWidth={2} />
              Request a revision
            </Button>
          )}
        </PanelCardBody>
      </PanelCard>
    )
  }

  return (
    <PanelCard>
      <PanelCardHead Icon={RotateCcw} title="Revision Requests">
        <StatusBadge tier="in_progress" label={max != null ? `${used} of ${max} used` : `${used} used`} />
      </PanelCardHead>
      <PanelCardBody className="bg-accent">
        <div className="rounded-2xl border border-border bg-card px-6 py-5">
          <div className="mb-5 grid grid-cols-2 gap-[18px] sm:grid-cols-4">
            {[
              ["Round", latest ? latest.label : `Revision ${used}`],
              ["Rounds left", remaining != null ? `${remaining}` : "—"],
              ["Status", statusLabel],
              ["Included", max != null ? `${max} rounds` : "—"],
            ].map(([k, v]) => (
              <div key={k}>
                <div className="text-[10.5px] font-bold tracking-[0.06em] text-ink-3 uppercase">
                  {k}
                </div>
                <div className="mt-1.5 text-[13.5px] font-semibold">{v}</div>
              </div>
            ))}
          </div>
          {/* Four step labels that cannot shrink, joined by `flex-1` connectors
              that can. The connectors collapse to zero and then the line simply
              stops giving: its min-content floor is ~371px, so wherever the
              panel is narrower the last node runs past the card's own
              `overflow-hidden` edge — measured 70px at 365, 15px at 420 and 68px
              at 861 (where the order page's tab rail drops the panel column to
              255px). Found by 78-muster-gate once the seed carried an order with
              a real revision round; before that this branch never rendered.
                `flex-wrap` is the whole fix: the line breaks between nodes
              instead of overflowing, and EVERY step label stays fully readable.
              The alternative — letting the steps shrink (`min-w-0`) — also stops
              the overflow but ellipsises the labels themselves (77px of text cut
              at 365, 81px at 861), which is why it is not what happens here.
              `gap-y-3` is rhythm for the wrapped case only; it is not what
              carries the overflow claim. Widths that already fit on one line are
              untouched: 768 and 1280 measure identically to before. */}
          <div className="flex flex-wrap items-center gap-0 gap-y-3">
            {STEPS.map((label, i) => (
              <Fragment key={label}>
                {i > 0 && <div className={cn("mx-3 h-0.5 flex-1", i <= now ? "bg-chart-2" : "bg-line-2")} />}
                <Step label={label} state={i < now ? "done" : i === now ? "now" : "pending"} />
              </Fragment>
            ))}
          </div>
          {rounds.length > 0 && (
            <ol className="mt-5 space-y-3 border-t border-border pt-5" aria-label="What you asked for">
              {[...rounds].reverse().map((round) => (
                <li
                  key={`${round.stage}-${round.number}`}
                  data-round={round.label}
                  className="rounded-xl border border-border bg-secondary px-4 py-3"
                >
                  <div className="flex items-baseline justify-between gap-3">
                    <span className="text-[12.5px] font-bold">{round.label}</span>
                    {round.requestedAt && <span className="text-[11px] text-ink-3">{round.requestedAt}</span>}
                  </div>
                  <p className="mt-1.5 whitespace-pre-wrap text-[12.5px] leading-[1.5] text-ink-2">
                    {round.notes || "No note was added."}
                  </p>
                </li>
              ))}
            </ol>
          )}
        </div>
      </PanelCardBody>
    </PanelCard>
  )
}
